import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { MapContainer, TileLayer, GeoJSON } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import PageHeader from "../components/PageHeader";
import { API_ENDPOINTS } from "../config/api";
import {
  LoadingState,
  ErrorState,
  EmptyState
} from "../components/CommonStates";
import PageContent from "../components/PageContent";
import { Card } from "../components/Card";
import {
  StateStats,
  StatCard,
  StatCardContent,
  StatValue,
  StatLabel,
  ComparisonText
} from "../components/Stats";
import { MapWrapper, MapBoundsFitter } from "../components/Map";
import { DataTable, TableRow, TableCell } from "../components/DataTable";

const ContentContainer = styled.div`
  display: flex;
  height: 100%;
  gap: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    height: auto;
  }
`;

const LeftPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  min-width: 0;
`;

const RightPanel = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  min-height: 0;
`;

const MapSection = styled.div`
  flex: 1;
  min-height: 400px;
`;

const ArticlesSection = styled.div`
  flex: 1;
  min-height: 400px;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const TitleCell = styled(TableCell)`
  font-weight: 500;
  color: #111827;
`;

const DateCell = styled(TableCell)`
  color: #6b7280;
  text-align: right;
  white-space: nowrap;
`;

const NewBadge = styled.span`
  background: #059669;
  color: white;
  padding: 0.125rem 0.375rem;
  border-radius: 3px;
  font-size: 0.625rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-left: 0.5rem;
`;

// Utility functions for calculating percentage differences from average
const calculateAverage = (allStatesData, metric) => {
  if (!allStatesData) return null;

  const validValues = allStatesData
    .filter(
      (state) =>
        state[metric] !== null &&
        state[metric] !== undefined &&
        state[metric] !== "" &&
        !isNaN(state[metric]) &&
        typeof state[metric] === "number"
    )
    .map((state) => state[metric]);

  if (validValues.length === 0) return null;

  return (
    validValues.reduce((sum, value) => sum + value, 0) / validValues.length
  );
};

const getPercentageDifferenceText = (
  allStatesData,
  currentStateData,
  metric,
  metricName,
  higherIsBetter = true
) => {
  if (
    !allStatesData ||
    !currentStateData ||
    currentStateData[metric] === null ||
    currentStateData[metric] === undefined
  ) {
    return null;
  }

  const average = calculateAverage(allStatesData, metric);
  if (average === null) return null;

  const currentValue = currentStateData[metric];
  const percentDiff = ((currentValue - average) / average) * 100;
  const absPercentDiff = Math.abs(percentDiff);

  // Handle very small differences
  if (absPercentDiff < 1) {
    return {
      text: "Near national average",
      className: "neutral",
      arrow: "→"
    };
  }

  const formattedPercent = Math.round(absPercentDiff);

  if (percentDiff > 0) {
    // Above average
    return {
      text: `${formattedPercent}% above national average`,
      className: higherIsBetter ? "positive" : "negative"
    };
  } else {
    // Below average
    return {
      text: `${formattedPercent}% below national average`,
      className: higherIsBetter ? "negative" : "positive"
    };
  }
};

function StateEbtTheftPage({ user, onLogout }) {
  const { stateName } = useParams();
  const navigate = useNavigate();

  // State for EBT theft data
  const [ebtData, setEbtData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentStateData, setCurrentStateData] = useState(null);

  // Map-related state
  const [geoJsonData, setGeoJsonData] = useState(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapError, setMapError] = useState(null);
  const [ebtTheftRatesData, setEbtTheftRatesData] = useState(null);
  const [theftRatesLoading, setTheftRatesLoading] = useState(false);
  const [theftRatesError, setTheftRatesError] = useState(null);
  const [mergedData, setMergedData] = useState(null);

  // State for articles
  const [articles, setArticles] = useState(null);
  const [articlesLoading, setArticlesLoading] = useState(false);
  const [articlesError, setArticlesError] = useState(null);

  // Format state name with proper capitalization
  const formattedStateName = stateName
    ? stateName
        .split("-")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ")
    : "";

  const handleBack = () => {
    navigate("/dashboard");
  };

  // Function to fetch GeoJSON data for county boundaries
  const fetchGeoJsonData = useCallback(async () => {
    setMapLoading(true);
    setMapError(null);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setMapError("Authentication required");
        setMapLoading(false);
        return;
      }

      const response = await fetch(
        API_ENDPOINTS.stateData(stateName, "county-shapes"),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setGeoJsonData(data.geoJson);
        } else {
          setMapError("Failed to load county boundaries");
        }
      } else {
        setMapError("Failed to load county boundaries from server");
      }
    } catch (error) {
      console.error("Error fetching GeoJSON data:", error);
      setMapError("Failed to load county boundaries");
    } finally {
      setMapLoading(false);
    }
  }, [stateName]);

  // Function to fetch EBT theft rates per county
  const fetchEbtTheftRates = useCallback(async () => {
    setTheftRatesLoading(true);
    setTheftRatesError(null);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setTheftRatesError("Authentication required");
        setTheftRatesLoading(false);
        return;
      }

      const response = await fetch(
        API_ENDPOINTS.stateData(stateName, "ebt-theft-rates-per-county"),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setEbtTheftRatesData(data);
        } else {
          setTheftRatesError("Failed to load EBT theft rates data");
        }
      } else {
        setTheftRatesError("Failed to load EBT theft rates data from server");
      }
    } catch (error) {
      console.error("Error fetching EBT theft rates data:", error);
      setTheftRatesError("Failed to load EBT theft rates data");
    } finally {
      setTheftRatesLoading(false);
    }
  }, [stateName]);

  // Function to fetch EBT theft articles
  const fetchArticles = useCallback(async () => {
    setArticlesLoading(true);
    setArticlesError(null);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setArticlesError("Authentication required");
        setArticlesLoading(false);
        return;
      }

      const response = await fetch(API_ENDPOINTS.articles("deep-dive"), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setArticles(data.articles);
        } else {
          setArticlesError("Failed to load EBT theft articles");
        }
      } else {
        setArticlesError("Failed to load EBT theft articles from server");
      }
    } catch (error) {
      console.error("Error fetching EBT theft articles:", error);
      setArticlesError("Failed to load EBT theft articles");
    } finally {
      setArticlesLoading(false);
    }
  }, []);

  // Function to merge GeoJSON and theft rates data
  const mergeMapData = useCallback(() => {
    if (!geoJsonData || !ebtTheftRatesData) {
      return;
    }

    // Create a map from FIPS code to theft rate data
    const fipsToTheftRate = ebtTheftRatesData.data?.reduce((acc, county) => {
      // The FIPS code comes as a number, convert to string with leading zeros if needed
      const fipsCode = county.FIPS
        ? county.FIPS.toString().padStart(5, "0")
        : null;

      if (fipsCode) {
        acc[fipsCode] = county;
      }
      return acc;
    }, {});

    setMergedData(fipsToTheftRate);
  }, [geoJsonData, ebtTheftRatesData]);

  // Function to get color based on theft rate (SCORE is in decimal format, e.g., 0.026 = 2.6%)
  const getColor = (score) => {
    if (!score || score === 0) return "#f7f7f7";

    // Convert decimal to percentage for thresholding
    const percentageRate = score * 100;

    // Color scale from light to dark red based on theft rate percentage
    const colors = [
      "#fee5d9", // 0-1%
      "#fcbba1", // 1-2%
      "#fc9272", // 2-3%
      "#fb6a4a", // 3-4%
      "#ef3b2c", // 4-5%
      "#cb181d", // 5-6%
      "#a50f15", // 6-7%
      "#67000d" // 7%+
    ];

    const thresholds = [0, 1, 2, 3, 4, 5, 6, 7];

    for (let i = thresholds.length - 1; i >= 0; i--) {
      if (percentageRate >= thresholds[i]) {
        return colors[i];
      }
    }

    return colors[0];
  };

  // Function to style GeoJSON features
  const styleFeature = (feature) => {
    const fipsCode = feature.properties.GEOID; // Use GEOID from GeoJSON data
    const countyData = mergedData?.[fipsCode];
    const score = countyData?.SCORE || 0; // SCORE comes as decimal (e.g., 0.026)

    // Use color based on theft rate score, or default gray if no data
    const defaultColor = "#e0e0e0"; // Light gray for no data
    const fillColor = score > 0 ? getColor(score) : defaultColor;

    return {
      fillColor: fillColor,
      weight: 1,
      opacity: 1,
      color: "#666",
      fillOpacity: 0.7
    };
  };

  // Function to handle feature interactions
  const onEachFeature = (feature, layer) => {
    const fipsCode = feature.properties.GEOID; // Use GEOID from GeoJSON data
    const countyName = feature.properties.NAME; // Use NAME from GeoJSON data
    const countyData = mergedData?.[fipsCode];

    if (countyData) {
      // Use SCORE field (decimal format) and convert to percentage for display
      const score = countyData.SCORE || 0;
      const theftRatePercent = score > 0 ? (score * 100).toFixed(2) : "0";
      const popupContent = `
        <div style="font-family: Arial, sans-serif;">
          <strong>${countyName} County</strong><br/>
          Theft Report Rate: ${theftRatePercent}%
        </div>
      `;
      layer.bindPopup(popupContent);
    } else {
      // Show basic info even if no theft rate data
      const popupContent = `
        <div style="font-family: Arial, sans-serif;">
          <strong>${countyName} County</strong><br/>
        </div>
      `;
      layer.bindPopup(popupContent);
    }
  };

  // Effect to merge data when both GeoJSON and theft rates data are available
  useEffect(() => {
    mergeMapData();
  }, [geoJsonData, ebtTheftRatesData, mergeMapData]);

  // Effect to fetch articles when component mounts
  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  // Fetch EBT theft stats data
  useEffect(() => {
    const fetchEbtData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(API_ENDPOINTS.ebtTheftStats, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json"
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setEbtData(data);

        // Find data for current state using STATE_NAME with robust matching
        if (data && data.data && formattedStateName) {
          // Normalize state name for better matching
          const normalizeStateName = (name) => {
            if (!name) return "";
            return name
              .toLowerCase()
              .trim()
              .replace(/\s+/g, " ") // Replace multiple spaces with single space
              .replace(/-/g, " "); // Replace hyphens with spaces
          };

          const targetStateName = normalizeStateName(formattedStateName);

          const stateData = data.data.find((state) => {
            if (!state.STATE_NAME) return false;

            const apiStateName = normalizeStateName(state.STATE_NAME);

            // Try exact match first
            if (apiStateName === targetStateName) {
              return true;
            }

            // Try common state name variations
            const stateAbbreviations = {
              alabama: "al",
              alaska: "ak",
              arizona: "az",
              arkansas: "ar",
              california: "ca",
              colorado: "co",
              connecticut: "ct",
              delaware: "de",
              florida: "fl",
              georgia: "ga",
              hawaii: "hi",
              idaho: "id",
              illinois: "il",
              indiana: "in",
              iowa: "ia",
              kansas: "ks",
              kentucky: "ky",
              louisiana: "la",
              maine: "me",
              maryland: "md",
              massachusetts: "ma",
              michigan: "mi",
              minnesota: "mn",
              mississippi: "ms",
              missouri: "mo",
              montana: "mt",
              nebraska: "ne",
              nevada: "nv",
              "new hampshire": "nh",
              "new jersey": "nj",
              "new mexico": "nm",
              "new york": "ny",
              "north carolina": "nc",
              "north dakota": "nd",
              ohio: "oh",
              oklahoma: "ok",
              oregon: "or",
              pennsylvania: "pa",
              "rhode island": "ri",
              "south carolina": "sc",
              "south dakota": "sd",
              tennessee: "tn",
              texas: "tx",
              utah: "ut",
              vermont: "vt",
              virginia: "va",
              washington: "wa",
              "west virginia": "wv",
              wisconsin: "wi",
              wyoming: "wy",
              "district of columbia": "dc"
            };

            // Check if target is full name and API is abbreviation
            const targetAbbr = stateAbbreviations[targetStateName];
            if (targetAbbr && apiStateName === targetAbbr) {
              return true;
            }

            // Check if target is abbreviation and API is full name
            const apiAbbr = stateAbbreviations[apiStateName];
            if (apiAbbr && targetStateName === apiAbbr) {
              return true;
            }

            return false;
          });

          setCurrentStateData(stateData);
        }
      } catch (err) {
        console.error("Error fetching EBT theft stats:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (stateName) {
      fetchEbtData();
      fetchGeoJsonData(); // Fetch county shapes
      fetchEbtTheftRates(); // Fetch EBT theft rates per county
    }
  }, [formattedStateName, stateName, fetchGeoJsonData, fetchEbtTheftRates]);

  // Check if leaflet CSS is loaded
  useEffect(() => {
    // Wait a bit for the CSS to be processed
    const checkCSS = () => {
      const leafletCSS = document.querySelector('link[href*="leaflet.css"]');
      const leafletStyles = document.querySelector("style[data-leaflet]");

      if (!leafletCSS && !leafletStyles) {
        // Try to inject the CSS manually if it's not found
        const style = document.createElement("style");
        style.setAttribute("data-leaflet", "true");
        style.textContent = `
          .leaflet-container {
            height: 100% !important;
            width: 100% !important;
          }
          .leaflet-control-zoom {
            display: none !important;
          }
          .leaflet-control-attribution {
            display: none !important;
          }
        `;
        document.head.appendChild(style);
      }
    };

    // Check immediately and after a short delay
    checkCSS();
    setTimeout(checkCSS, 100);
  }, []);

  return (
    <>
      <PageHeader
        title={`${formattedStateName} EBT Theft Prevention × Propel`}
        subtitle={
          ebtData
            ? `Last updated ${new Date(ebtData.lastUpdated).toLocaleDateString(
                "en-US",
                {
                  year: "numeric",
                  month: "long",
                  day: "numeric"
                }
              )}`
            : undefined
        }
        showBackButton={true}
        onBack={handleBack}
      />
      <PageContent
        $fullWidth
        style={{
          marginTop: "0",
          paddingTop: "calc(80px + 32px)",
          height: "100vh"
        }}
      >
        <ContentContainer>
          <LeftPanel>
            <Card style={{ height: "100%" }}>
              {loading && (
                <LoadingState>Loading EBT theft statistics...</LoadingState>
              )}
              {error && <ErrorState>Error loading data: {error}</ErrorState>}
              {ebtData && !loading && currentStateData ? (
                <>
                  <StateStats className="ebt-theft-layout">
                    <StatCard $compact>
                      <StatCardContent>
                        <StatValue>
                          {currentStateData.CURRENT_MAU?.toLocaleString()}
                        </StatValue>
                        <StatLabel>
                          Monthly active Propel users in {formattedStateName}
                        </StatLabel>
                      </StatCardContent>
                    </StatCard>

                    <StatCard $compact>
                      <StatCardContent $compact>
                        <StatValue>
                          {Math.round(
                            currentStateData.MARKET_PENETRATION * 100
                          )}
                          %
                        </StatValue>
                        <StatLabel>SNAP households use Propel</StatLabel>
                        {(() => {
                          const comparison = getPercentageDifferenceText(
                            ebtData.data,
                            currentStateData,
                            "MARKET_PENETRATION",
                            "Market Penetration",
                            true // higher penetration is better
                          );
                          return (
                            <ComparisonText
                              className={comparison?.className || "neutral"}
                            >
                              {comparison ? (
                                <>
                                  {comparison.arrow} {comparison.text}
                                </>
                              ) : (
                                "Comparison data unavailable"
                              )}
                            </ComparisonText>
                          );
                        })()}
                      </StatCardContent>
                    </StatCard>

                    <StatCard $compact>
                      <StatCardContent>
                        <StatValue color="#dc2626" size="large">
                          –$
                          {Math.round(
                            currentStateData.USD_THEFT || 0
                          ).toLocaleString()}
                        </StatValue>
                        <StatLabel>
                          {(() => {
                            const totalTheft = currentStateData.USD_THEFT;
                            const oosTheft = currentStateData.USD_THEFT_OOS;
                            if (totalTheft && oosTheft) {
                              const percentage = (oosTheft / totalTheft) * 100;
                              return `Funds stolen in the last 120 days (${Math.round(
                                percentage
                              )}% out of state)`;
                            }
                            return "USD Theft";
                          })()}
                        </StatLabel>
                      </StatCardContent>
                    </StatCard>

                    <StatCard $compact>
                      <StatCardContent $compact>
                        <StatValue>
                          {Math.round(
                            currentStateData.FRAC_WITH_SECURITY_FEATURES * 100
                          )}
                          %
                        </StatValue>
                        <StatLabel>
                          Use security features available through Propel
                        </StatLabel>
                        {(() => {
                          const comparison = getPercentageDifferenceText(
                            ebtData.data,
                            currentStateData,
                            "FRAC_WITH_SECURITY_FEATURES",
                            "Security Features",
                            true // higher security is better
                          );
                          return (
                            <ComparisonText
                              className={comparison?.className || "neutral"}
                            >
                              {comparison ? (
                                <>
                                  {comparison.arrow} {comparison.text}
                                </>
                              ) : (
                                "Comparison data unavailable"
                              )}
                            </ComparisonText>
                          );
                        })()}
                      </StatCardContent>
                    </StatCard>
                  </StateStats>
                </>
              ) : (
                <EmptyState>
                  No data available for {formattedStateName}
                </EmptyState>
              )}
            </Card>
          </LeftPanel>

          <RightPanel>
            <MapSection>
              <Card style={{ height: "100%", padding: "0" }}>
                <MapWrapper
                  height="100%"
                  $minHeight="400px"
                  $noBorder
                  $noShadow
                >
                  {(mapLoading || theftRatesLoading) && (
                    <LoadingState>Loading map...</LoadingState>
                  )}
                  {mapError && <ErrorState>{mapError}</ErrorState>}
                  {(() => {
                    if (
                      geoJsonData &&
                      !mapLoading &&
                      !theftRatesLoading &&
                      !mapError
                    ) {
                      return (
                        <MapContainer
                          key="map-container"
                          center={[36.7783, -119.4179]} // California center
                          zoom={6}
                          scrollWheelZoom={false}
                          zoomControl={false}
                          doubleClickZoom={false}
                          style={{ height: "100%", width: "100%" }}
                        >
                          <TileLayer
                            url="https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png"
                            attribution=""
                          />
                          <GeoJSON
                            data={geoJsonData}
                            style={styleFeature}
                            onEachFeature={onEachFeature}
                            key={JSON.stringify(mergedData)}
                          />
                          <MapBoundsFitter geoJsonData={geoJsonData} />
                        </MapContainer>
                      );
                    }
                    return null;
                  })()}
                </MapWrapper>

                {theftRatesError && (
                  <div
                    style={{
                      background: "#f8d7da",
                      color: "#721c24",
                      padding: "0.75rem",
                      borderRadius: "0.5rem",
                      marginTop: "1rem"
                    }}
                  >
                    {theftRatesError}
                  </div>
                )}
              </Card>
            </MapSection>

            <ArticlesSection>
              <Card
                style={{
                  flex: 1,
                  padding: "1.5rem",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0
                }}
              >
                {articlesLoading && (
                  <LoadingState>Loading articles...</LoadingState>
                )}
                {articlesError && <ErrorState>{articlesError}</ErrorState>}
                {articles && articles.length > 0 ? (
                  <div
                    style={{
                      flex: 1,
                      overflow: "auto",
                      minHeight: 0
                    }}
                  >
                    <DataTable>
                      {articles
                        .sort((a, b) => {
                          // Sort by date, newest first
                          const dateA = new Date(a.date);
                          const dateB = new Date(b.date);
                          return dateB - dateA;
                        })
                        .slice(0, 8) // Only show the last 8 articles
                        .map((article, index) => {
                          // Check if article is from last 7 days
                          const articleDate = new Date(article.date);
                          const sevenDaysAgo = new Date();
                          sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
                          const isNew = articleDate > sevenDaysAgo;

                          return (
                            <TableRow
                              key={index}
                              onClick={() => window.open(article.url, "_blank")}
                              columns="1fr auto"
                              style={{ cursor: "pointer" }}
                            >
                              <TitleCell>
                                {article.title}
                                {isNew && <NewBadge>New</NewBadge>}
                              </TitleCell>
                              <DateCell>{article.date}</DateCell>
                            </TableRow>
                          );
                        })}
                    </DataTable>
                  </div>
                ) : (
                  <EmptyState>No articles found.</EmptyState>
                )}
              </Card>
            </ArticlesSection>
          </RightPanel>
        </ContentContainer>
      </PageContent>
    </>
  );
}

export default StateEbtTheftPage;
